/**
 * useAuth Hook
 *
 * Custom hook that provides access to authentication context.
 * Must be used within an AuthProvider component.
 */

import { useContext } from 'react';
import { AuthContext, type AuthContextValue } from '@/contexts/AuthContext';

/**
 * Hook to access authentication state and methods
 *
 * @returns AuthContextValue containing user, loading state, and auth methods
 * @throws Error if used outside of AuthProvider
 *
 * @example
 * ```tsx
 * const { user, loading, signIn, signOut } = useAuth();
 *
 * if (loading) {
 *   return <LoadingSpinner />;
 * }
 *
 * if (!user) {
 *   return <button onClick={signIn}>Sign In</button>;
 * }
 *
 * return <button onClick={signOut}>Sign Out</button>;
 * ```
 */
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
